'use client';

import Link from 'next/link';
import { ChevronDown, MobileDevice } from '@/components/pages/icons';
import Cta from '@/components/pages/cta';
import { MobileSubmenuLink } from './mobile-submenu-link';
import type { DesktopMenu, MobileSubMenuProps, SubmenuSection } from './type';

function dropdownKey(item: { isClaimSections?: boolean; isLocations?: boolean }): DesktopMenu | null {
  if (item.isLocations) return 'locations';
  if (item.isClaimSections) return 'claim';
  return null;
}

export function MobileSubMenu({
  navLinks,
  menus,
  ctaHref,
  onClickCta,
  showFreeCaseReviewButton = true,
}: MobileSubMenuProps) {
  return (
    // The drawer scrolls on its own — the top bar is 56px tall, so the list is
    // capped to the remaining viewport height (the parent clips at 100svh).
    <nav
      data-menu
      className="hidden group-open:flex group-open:@lg:hidden flex-col overflow-y-auto overscroll-contain max-h-[calc(100svh-56px)]"
    >
      <ul className="divide-y px-2 flex flex-col divide-black/20 text-[#172554]">
        <li className="font-serif text-[52px] italic leading-[110%] tracking-[-1.04px] p-4">
          Menu
        </li>

        {(navLinks || []).map((item) => {
          const key = dropdownKey(item);
          if (!key) {
            return (
              <MobileSubmenuLink
                key={`mobile-${item.label}`}
                label={item.label}
                href={item.href || '#'}
              />
            );
          }

          const sections: SubmenuSection[] = menus[key]?.sections ?? [];

          return (
            <li key={`mobile-${item.label}`}>
              <details className="group/details">
                <summary className="text-[32px] leading-[120%] tracking-[-0.96px] px-4 py-6 flex justify-between w-full items-center cursor-pointer">
                  <span>{item.label}</span>
                  <ChevronDown className="w-6 h-6 text-brand-accent transition-transform duration-300 ease-in-out group-open/details:-rotate-180" />
                </summary>
                <div className="px-4 pb-6 max-h-[300px] overflow-y-auto flex flex-col gap-4">
                  {sections.map((section, index) => (
                    <div key={section.title ?? `section-${index}`}>
                      {section.title && (
                        <h4 className="text-sm font-medium tracking-[0.21px] text-gray-light mb-2">
                          {section.title}
                        </h4>
                      )}
                      <ul className="grid grid-cols-2 gap-4">
                        {section.items.map((sub) => (
                          <li key={`mobile-${sub.url}`}>
                            <Link
                              className="text-brand-text-button text-xl leading-[130%] tracking-[-0.4px] hover:brightness-90"
                              href={sub.url}
                            >
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            </li>
          );
        })}

        {showFreeCaseReviewButton && (
          <li className="px-4 py-3">
            <Cta
              size="md"
              variation="outline"
              text="Free case review"
              iconSide="left"
              icon={<MobileDevice />}
              className="w-full"
              onClick={onClickCta}
              href={ctaHref}
            />
          </li>
        )}
      </ul>
    </nav>
  );
}
